/** Countries, provinces and currencies for ship-to and market settings (names from Intl). */

/** ISO 3166-1 alpha-2 codes. */
const COUNTRY_CODES = (
  "AD AE AF AG AI AL AM AO AQ AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ BR BS BT BV BW BY BZ " +
  "CA CC CD CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ DE DJ DK DM DO DZ EC EE EG EH ER ES ET FI FJ FK FM FO FR " +
  "GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GS GT GU GW GY HK HM HN HR HT HU ID IE IL IM IN IO IQ IR IS IT JE JM JO " +
  "JP KE KG KH KI KM KN KP KR KW KY KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML MM MN MO MP MQ MR " +
  "MS MT MU MV MW MX MY MZ NA NC NE NF NG NI NL NO NP NR NU NZ OM PA PE PF PG PH PK PL PM PN PR PS PT PW PY QA RE RO " +
  "RS RU RW SA SB SC SD SE SG SH SI SJ SK SL SM SN SO SR SS ST SV SX SY SZ TC TD TF TG TH TJ TK TL TM TN TO TR TT TV " +
  "TW TZ UA UG UM US UY UZ VA VC VE VG VI VN VU WF WS YE YT ZA ZM ZW"
).split(" ");

function displayNames(type: "region" | "currency"): Intl.DisplayNames | null {
  try {
    return new Intl.DisplayNames(["en"], { type });
  } catch {
    return null;
  }
}

const regionNames = displayNames("region");
const currencyNames = displayNames("currency");

export function countryName(code: string): string {
  return regionNames?.of(code.toUpperCase()) ?? code.toUpperCase();
}

export interface Choice {
  value: string;
  label: string;
}

/** All countries, sorted by English name. */
export const COUNTRIES: readonly Choice[] = COUNTRY_CODES.map((code) => ({
  value: code,
  label: countryName(code),
})).sort((a, b) => a.label.localeCompare(b.label));

export const CANADIAN_PROVINCES: readonly Choice[] = [
  { value: "AB", label: "Alberta" },
  { value: "BC", label: "British Columbia" },
  { value: "MB", label: "Manitoba" },
  { value: "NB", label: "New Brunswick" },
  { value: "NL", label: "Newfoundland and Labrador" },
  { value: "NS", label: "Nova Scotia" },
  { value: "NT", label: "Northwest Territories" },
  { value: "NU", label: "Nunavut" },
  { value: "ON", label: "Ontario" },
  { value: "PE", label: "Prince Edward Island" },
  { value: "QC", label: "Quebec" },
  { value: "SK", label: "Saskatchewan" },
  { value: "YT", label: "Yukon" },
];

const US_STATE_NAMES =
  "AL:Alabama AK:Alaska AZ:Arizona AR:Arkansas CA:California CO:Colorado CT:Connecticut DE:Delaware DC:District_of_Columbia " +
  "FL:Florida GA:Georgia HI:Hawaii ID:Idaho IL:Illinois IN:Indiana IA:Iowa KS:Kansas KY:Kentucky LA:Louisiana ME:Maine " +
  "MD:Maryland MA:Massachusetts MI:Michigan MN:Minnesota MS:Mississippi MO:Missouri MT:Montana NE:Nebraska NV:Nevada " +
  "NH:New_Hampshire NJ:New_Jersey NM:New_Mexico NY:New_York NC:North_Carolina ND:North_Dakota OH:Ohio OK:Oklahoma " +
  "OR:Oregon PA:Pennsylvania RI:Rhode_Island SC:South_Carolina SD:South_Dakota TN:Tennessee TX:Texas UT:Utah VT:Vermont " +
  "VA:Virginia WA:Washington WV:West_Virginia WI:Wisconsin WY:Wyoming";

export const US_STATES: readonly Choice[] = US_STATE_NAMES.split(" ").map((entry) => {
  const [value = "", label = ""] = entry.split(":");
  return { value, label: label.replaceAll("_", " ") };
});

/** Province or state choices for countries where a fixed list is expected. */
export function regionsFor(country: string): readonly Choice[] | null {
  if (country === "CA") return CANADIAN_PROVINCES;
  if (country === "US") return US_STATES;
  return null;
}

const COMMON_CURRENCIES = [
  "CAD",
  "USD",
  "EUR",
  "GBP",
  "CHF",
  "AUD",
  "NZD",
  "JPY",
  "SEK",
  "NOK",
  "DKK",
  "PLN",
  "CZK",
  "SGD",
  "HKD",
  "INR",
  "MXN",
];

export function currencyChoices(current?: string): Choice[] {
  const codes = current && !COMMON_CURRENCIES.includes(current) ? [current, ...COMMON_CURRENCIES] : COMMON_CURRENCIES;
  return codes.map((code) => ({ value: code, label: `${code} – ${currencyNames?.of(code) ?? code}` }));
}

/** Canadian postal code: letter-digit-letter, optional space, digit-letter-digit (V6B 1A1). */
export function isCanadianPostalCode(value: string): boolean {
  return /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/.test(value.trim());
}
